import { createApi } from '@redux/createApi';
import { createSlice } from '@reduxjs/toolkit';
import { serialize } from '../../../shared/lib/serialize';

export const tags = {
  getSanctions: 'getSanctions',
  getSanctionsUsersWithMatches: 'getSanctionsUsersWithMatches',
  getSanctionsUsersWithMatchesDetails: 'getSanctionsUsersWithMatchesDetails',
  manualBlocking: 'manualBlocking',
};

export const sanctionsApi = createApi({
  reducerPath: 'sanctionsApi',
  tagTypes: [
    tags.getSanctions,
    tags.getSanctionsUsersWithMatches,
    tags.getSanctionsUsersWithMatchesDetails,
  ],
  endpoints: (builder) => ({
    getSanctions: builder.query({
      query: () => ({
        method: 'get',
        url: '/compliance-module/gsfr/list',
      }),
      providesTags: [tags.getSanctions],
      transformResponse: (data) => {
        // Mock data for testing
        return data
      },
    }),
    getSanctionsUsersWithMatchesDetails: builder.query({
      query: ({ userId }) => {
        return {
          method: 'get',
          url: `/compliance-module/match-user-details?${serialize({
            user_id: userId,
          })}`,
        };
      },
      invalidatesTags: [tags.getSanctionsUsersWithMatchesDetails],
    }),
    getSanctionsUsersWithMatches: builder.query({
      query: (queryParams) => {
        const [page, limit, matchType] = queryParams;
        return {
          method: 'get',
          url: `/compliance-module/matches?${serialize({
            page_number: page,
            page_size: limit,
            ...(matchType === 'ALL' ? {} : { match_type: matchType }),
          })}`,
        };
      },
      providesTags: [tags.getSanctionsUsersWithMatches],
      serializeQueryArgs: ({ queryArgs }) => {
        return { ...queryArgs };
      },
      transformResponse: (data) => {
        return {
          list: data.matched_users,
          count: data.page_amount,
        };
      },
    }),
    manualBlocking: builder.mutation({
      query: ({ userId, block }) => {
        return {
          method: 'post',
          url: `/compliance-module/manual-blocking`,
          body: {
            user_id: userId,
            block,
          },
        };
      },
      providesTags: [tags.manualBlocking],
      invalidatesTags: [
        tags.getSanctionsUsersWithMatchesDetails,
        tags.getSanctionsUsersWithMatches,
      ],
    }),
    downloadSanctionsList: builder.mutation({
      query: () => ({
        method: 'get',
        url: 'https://fiu.gov.kg/uploads/6735ced630865.xml',
        responseHandler: 'text',
      }),
      invalidatesTags: [tags.getSanctions],
    }),
  }),
});

export const sanctionsUsersWithMatchesParams = createSlice({
  name: 'usersWithMatchesParams',
  initialState: {
    page: 1,
    limit: 100,
    matchType: 'ALL',
  },
  reducers: {
    setParams: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
});

export const {
  useGetSanctionsQuery,
  useExportFileSanctionsListMutation,
  useGetSanctionsUsersWithMatchesQuery,
  useGetSanctionsUsersWithMatchesDetailsQuery,
  useManualBlockingMutation,
  useDownloadSanctionsListMutation,
} = sanctionsApi;

export const { setParams } = sanctionsUsersWithMatchesParams.actions;
export const sanctionsUsersWithMatchesParamsReducer =
  sanctionsUsersWithMatchesParams.reducer;
export const usersWithMatchesParamsSelector = (state) =>
  state.sanctionsUsersWithMatchesParams;

export const uploadSanctionsUrl = async (urls) => {
  const response = await fetch('/compliance-module/xml-upload/upload-url', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(urls),
  });

  if (!response.ok) {
    throw new Error('Failed to upload URLs');
  }

  return response.json();
};
